import React, { Fragment } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import Link from '@atlaskit/link/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Grid } from '@atlaskit/primitives/compiled/grid';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import TabPanel from '@atlaskit/tabs/tab-panel';
import Tag from '@atlaskit/tag/removable-tag';

import type { NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { CopyButton } from '../../common/ui/copy-button';
import { getPluginTagColor } from '../get-plugin-tag-color';
import { messages } from '../messages';
import type { RequestOverviewData } from '../types';
import { Panel } from './panel-wrapper';
import { SplunkLink } from './splunk-link';

const styles = cssMap({
	twoColumnGrid: {
		gridTemplateColumns: '1fr 1fr',
	},
});

type RequestOverviewPanelProps = RequestOverviewData & {
	isNaturalLanguageQuery: boolean;
};

export const RequestOverviewPanel: ({
	originalQuery,
	transformedQuery,
	traceId,
	intentDetectionTraceId,
	intentDetectionTimestamp,
	originalIntent,
	finalRoutingDecision,
	detectionMetadata,
	modelName,
	cacheHit,
	sources,
	isNaturalLanguageQuery,
	invokedPluginNames,
}: RequestOverviewPanelProps) => React.JSX.Element = ({
	originalQuery,
	transformedQuery,
	traceId,
	intentDetectionTraceId,
	intentDetectionTimestamp,
	originalIntent,
	finalRoutingDecision,
	detectionMetadata,
	modelName,
	cacheHit,
	sources,
	isNaturalLanguageQuery,
	invokedPluginNames,
}: RequestOverviewPanelProps) => {
	const { formatMessage } = useIntl();

	const intentDetectionTraceIdDiverged =
		!!intentDetectionTraceId && !!traceId && intentDetectionTraceId !== traceId;

	const displayCacheHit = cacheHit === null ? 'disabled' : cacheHit.toString();

	return (
		<TabPanel>
			<Panel testId="sain-online-debugger-request-overview-panel">
				<Grid xcss={styles.twoColumnGrid} gap="space.100">
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_original_query_subheading)}
						</Heading>
						<Text>{originalQuery ?? 'null'}</Text>
					</Box>
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_transformed_query_subheading)}
						</Heading>
						<Text>{transformedQuery ?? 'null'}</Text>
					</Box>
				</Grid>
				<Grid xcss={styles.twoColumnGrid} gap="space.100">
					<Box testId="sain-online-debugger-original-intent">
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_original_intent_subheading)}
						</Heading>
						<Text>{originalIntent ?? 'null'}</Text>
					</Box>
					<Box testId="sain-online-debugger-final-routing-decision">
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_detected_intent_subheading)}
						</Heading>
						<Text>{finalRoutingDecision ?? 'null'}</Text>
					</Box>
					{intentDetectionTraceId && (
						<Box testId="sain-online-debugger-intent-detection-trace-id">
							<Heading size="xsmall">
								{formatMessage(messages.ai_debugger_modal_intent_detection_trace_id_subheading)}
							</Heading>
							<Inline space="space.100" alignBlock="start">
								<Text>{intentDetectionTraceId}</Text>
								<CopyButton
									copyText={intentDetectionTraceId}
									copyLabel={formatMessage(
										messages.ai_debugger_modal_copy_intent_detection_trace_id_label,
									)}
									copiedLabel={formatMessage(messages.ai_debugger_modal_trace_id_copied_text)}
									testId="sain-online-debugger-copy-intent-detection-trace-id-button"
								/>
								<SplunkLink
									traceId={intentDetectionTraceId}
									timestamp={intentDetectionTimestamp}
									variant="intentDetection"
									testId="sain-online-debugger-intent-detection-trace-id-splunk-link"
								/>
							</Inline>
							{intentDetectionTraceIdDiverged && (
								<Text size="small" color="color.text.warning">
									{formatMessage(messages.ai_debugger_modal_intent_detection_trace_id_diverged)}
								</Text>
							)}
						</Box>
					)}
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_answer_trace_id_subheading)}
						</Heading>
						<Inline space="space.100" alignBlock="start">
							<Text>{traceId ?? 'null'}</Text>
							{traceId && (
								<CopyButton
									copyText={traceId}
									copyLabel={formatMessage(messages.ai_debugger_modal_copy_trace_id_label)}
									copiedLabel={formatMessage(messages.ai_debugger_modal_trace_id_copied_text)}
									testId="sain-online-debugger-copy-trace-id-button"
								/>
							)}
							<SplunkLink
								traceId={traceId}
								timestamp={intentDetectionTimestamp}
								testId="sain-online-debugger-answer-trace-id-splunk-link"
							/>
						</Inline>
					</Box>
					{isNaturalLanguageQuery && (
						<Fragment>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_model_subheading)}
								</Heading>
								<Text>{modelName}</Text>
							</Box>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_cached_answer_subheading)}
								</Heading>
								<Text>{displayCacheHit}</Text>
							</Box>
						</Fragment>
					)}
				</Grid>
				{detectionMetadata && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_detection_metadata_subheading)}
						</Heading>
						<Grid xcss={styles.twoColumnGrid} gap="space.100">
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_detection_method_label)}
								</Heading>
								<Text>{detectionMetadata.method ?? 'null'}</Text>
							</Box>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_detection_model_label)}
								</Heading>
								<Text>{detectionMetadata.model ?? 'null'}</Text>
							</Box>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_detection_latency_label)}
								</Heading>
								<Text>{detectionMetadata.latencyMs ?? 'null'}</Text>
							</Box>
							<Box>
								<Heading size="xsmall">
									{formatMessage(messages.ai_debugger_modal_detection_cache_hit_label)}
								</Heading>
								<Text>
									{detectionMetadata.cacheHit === null
										? 'null'
										: detectionMetadata.cacheHit.toString()}
								</Text>
							</Box>
						</Grid>
					</Box>
				)}
				{/* Plugins invoked - only show if there are plugins */}
				{isNaturalLanguageQuery && invokedPluginNames.length > 0 && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_plugins_invoked_subheading)}
						</Heading>
						<Inline space="space.100" alignBlock="center">
							{invokedPluginNames.map((pluginName) => (
								<Tag
									key={pluginName}
									text={pluginName}
									color={getPluginTagColor(pluginName)}
									isRemovable={false}
									migration_fallback="lozenge"
								/>
							))}
						</Inline>
					</Box>
				)}
				{/* Sources used - only show if there are sources */}
				{isNaturalLanguageQuery && sources.length > 0 && (
					<Box>
						<Heading size="xsmall">
							{formatMessage(messages.ai_debugger_modal_sources_subheading)}
						</Heading>
						{sources.map((source: NLPSearchResultSource, index: number) => (
							<Inline key={source.ari ?? source.id} space="space.050" alignBlock="center">
								<Text>{index + 1}.</Text>
								<Link href={source.url} target="_blank">
									{source.title}
								</Link>
							</Inline>
						))}
					</Box>
				)}
			</Panel>
		</TabPanel>
	);
};
